<template>
    <AppModal ref="modal" :title="$t('generateProductKeyWordsModal.title')" size="full" @on-hide="hide">
        <template v-if="shouldShowErrorTable">
            <div class="table-responsive table-scrollable">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th>{{ $t('labels.copy_products_request') }}</th>
                            <th>{{ $t('labels.copy_products_response') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="badHttpHistoryItem in badHttpHistory">
                            <td><span class="text-nowrap">{{ badHttpHistoryItem.request.body }}</span></td>
                            <td><span class="text-nowrap">{{ badHttpHistoryItem.response.body }}</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <WizardForm :steps="steps" @submit="saveKeywords" v-else />
    </AppModal>
</template>
<script>
import { defineAsyncComponent } from 'vue';
import AppModal from '../../../../../../components/new/modal/AppModal.vue';
import WizardForm from '../../../../../../components/new/form/wizardForm/WizardForm.vue';

import { httpClient } from '../../../../../../services/HttpClient';
import HttpRequest from '../../../../../../services/Http/HttpRequest';
import HttpSender from '../../../../../../services/Http/HttpSender';

export default {
    name: "GenerateProductKeyWordsModal",
    components: {
        AppModal, WizardForm
    },
    props: {
        integrationLanguages: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            collectedInfoByTypeUuid: null,
            selectedAttributeUuidsByTypeUuid: {},
            groupedAttributeUuidsByTypeUuid: {},
            keywordsByTypeUuid: {},
            savingSettingsByTypeUuid: {},
            badHttpHistory: [],
            httpSender: new HttpSender(),
            processingStarted: false
        }
    },
    methods: {
        async show(productUuids) {
            this.collectedInfoByTypeUuid = null;
            this.selectedAttributeUuidsByTypeUuid = {};
            this.groupedAttributeUuidsByTypeUuid = {};

            this.$refs.modal.show();

            let products = await this.fetchProducts(productUuids);
            this.collectedInfoByTypeUuid = this.collectInfoByTypeUuid(products);
        },
        hide() {
            this.$refs.modal.hide();
            this.collectedInfoByTypeUuid = null;
            this.selectedAttributeUuidsByTypeUuid = {};
            this.groupedAttributeUuidsByTypeUuid = {};
            this.keywordsByTypeUuid = {};
            this.savingSettingsByTypeUuid = {};
            this.badHttpHistory = [];
            this.httpSender = new HttpSender();
            this.processingStarted = false;
        },
        async fetchProducts(productUuids) {
            return await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                uuids: productUuids,
                extra_fields: ['integrationFilterItems'],
                limit: 100
            }).then(response => response.data.data);
        },
        collectInfoByTypeUuid(products) {
            const result = {};

            for (const product of products) {
                const { product: internalProduct, uuid: pUuid, integration_filter_items: items = [] } = product;

                if (!internalProduct || !internalProduct.category) {
                    continue;
                }

                const category = internalProduct.category;
                const typeUuid = category.uuid;

                if (!result[typeUuid]) {
                    result[typeUuid] = {
                        name: category.name,
                        products: {},
                        attributes: {}
                    };
                }

                const typeEntry = result[typeUuid];

                typeEntry.products[pUuid] = {
                    id: product.id,
                    name: product.name,
                    sku: product.sku,
                    image_url: product.picture_url,
                    attributeItemUuids: items.map(item => item.uuid),
                    seo: product.seo
                };

                const seenAttributesInProduct = new Set();

                for (const item of items) {
                    const attribute = item.integration_filter;

                    const fUuid = attribute.uuid;
                    const iUuid = item.uuid;

                    if (!typeEntry.attributes[fUuid]) {
                        typeEntry.attributes[fUuid] = { name: attribute.name, productsCount: 0, items: {} };
                    }

                    if (!typeEntry.attributes[fUuid].items[iUuid]) {
                        typeEntry.attributes[fUuid].items[iUuid] = { name: item.name, productsCount: 0 };
                    }

                    typeEntry.attributes[fUuid].items[iUuid].productsCount++;
                    seenAttributesInProduct.add(fUuid);
                }

                seenAttributesInProduct.forEach(fUuid => {
                    typeEntry.attributes[fUuid].productsCount++;
                });
            }

            return result;
        },
        syncGroupsWithSelection() {
            let rebuiltGroupedAttributeUuidsByTypeUuid = {};

            for (const typeUuid in this.groupedAttributeUuidsByTypeUuid) {
                const groupedAttributeUuids = this.groupedAttributeUuidsByTypeUuid[typeUuid];
                const selectedAttributeUuids = this.selectedAttributeUuidsByTypeUuid[typeUuid] || [];

                const rebuiltGroupedAttributeUuids = groupedAttributeUuids.filter(group => {
                    return group.every(uuid => selectedAttributeUuids.includes(uuid));
                });

                if (!rebuiltGroupedAttributeUuids.length) {
                    continue;
                }

                rebuiltGroupedAttributeUuidsByTypeUuid[typeUuid] = rebuiltGroupedAttributeUuids
            }

            this.groupedAttributeUuidsByTypeUuid = rebuiltGroupedAttributeUuidsByTypeUuid;
        },
        async saveKeywords() {
            this.processingStarted = true;
            let requests = [];
            
            for (const [typeUuid, collectedInfo] of Object.entries(this.collectedInfoByTypeUuid)) {
                const keywordsData = this.keywordsByTypeUuid[typeUuid];
                let productKeywordsByProductUuid = {};
                
                for (const [productUuid, product] of Object.entries(collectedInfo.products)) {
                    if (this.savingSettingsByTypeUuid[typeUuid]?.syncKeywords === undefined || !this.savingSettingsByTypeUuid[typeUuid].syncKeywords) {
                        productKeywordsByProductUuid[productUuid] = { ...product.seo }
                    }

                    for (let language of this.integrationLanguages) {
                        if (!productKeywordsByProductUuid[productUuid]) {
                            productKeywordsByProductUuid[productUuid] = {};
                        }

                        const typeOfProductKeyword = keywordsData?.typeOfProduct ? keywordsData.typeOfProduct[language] : null;
                        const synonymForTypeOfProductKeyword = keywordsData?.synonymForTypeOfProduct ? keywordsData.synonymForTypeOfProduct[language] : null;
                        const forWhoIsThisProductKeyword = keywordsData?.forWhoIsThisProduct ? keywordsData.forWhoIsThisProduct[language] : null;
                        const forWhatIsThisProductKeyword = keywordsData?.forWhatIsThisProduct ? keywordsData.forWhatIsThisProduct[language] : null;

                        if (typeOfProductKeyword) {
                            productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${typeOfProductKeyword}` : typeOfProductKeyword;
                        }

                        if (synonymForTypeOfProductKeyword) {
                            productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${synonymForTypeOfProductKeyword}` : synonymForTypeOfProductKeyword;
                        }

                        if (forWhoIsThisProductKeyword) {
                            productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${forWhoIsThisProductKeyword}` : forWhoIsThisProductKeyword;
                        }

                        if (forWhatIsThisProductKeyword) {
                            productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${forWhatIsThisProductKeyword}` : forWhatIsThisProductKeyword;
                        }
                    }

                    for (const [attributeUuid, keyword] of Object.entries(this.keywordsByTypeUuid[typeUuid]?.byAttributeItemUuids || {})) {
                        let separatedUuids = attributeUuid.split('_');
                        if (separatedUuids.length === 1) {
                            if (product.attributeItemUuids?.includes(attributeUuid)) {
                                for (let language of this.integrationLanguages) {
                                    if (keyword[language]) {
                                        productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${keyword[language]}` : keyword[language];
                                    }
                                }
                            }
                        } else if (separatedUuids.length > 1) {
                            if (separatedUuids.every(uuid => product.attributeItemUuids?.includes(uuid))) {
                                for (let language of this.integrationLanguages) {
                                    if (keyword[language]) {
                                        productKeywordsByProductUuid[productUuid][language] = productKeywordsByProductUuid[productUuid][language] ? productKeywordsByProductUuid[productUuid][language] + `, ${keyword[language]}` : keyword[language];
                                    }
                                }
                            }
                        }
                    }                   
                }

                if (Object.keys(productKeywordsByProductUuid).length > 0) {
                    for (const [productUuid, keywordsByLanguage] of Object.entries(productKeywordsByProductUuid)) {
                        requests.push(new HttpRequest('PATCH', `/integrations/products/${productUuid}/`, {
                            seos: keywordsByLanguage
                        }));
                    }
                }
            }

            await this.httpSender.sendRequestsInBatches(requests, () => {});

            this.badHttpHistory = this.httpSender.getHistory().filter((historyItem) => {
                return historyItem.response.status !== 201 && historyItem.response.status !== 200;
            });

            if (this.badHttpHistory.length === 0) {
                this.hide();
            }
        }
    },
    computed: {
        steps() {
            return [
                {
                    name: this.$t('generateProductKeyWordsModal.steps.SelectAttributesStep'),
                    component: defineAsyncComponent(() => import('./wizardSteps/SelectAttributesStep.vue')),
                    props: {
                        collectedInfoByTypeUuid: this.collectedInfoByTypeUuid,
                        selectedAttributeUuidsByTypeUuid: this.selectedAttributeUuidsByTypeUuid
                    }
                },
                {
                    name: this.$t('generateProductKeyWordsModal.steps.SettingAttributesStep'),
                    component: defineAsyncComponent(() => import('./wizardSteps/SettingAttributesStep.vue')),
                    props: {
                        collectedInfoByTypeUuid: this.collectedInfoByTypeUuid,
                        selectedAttributeUuidsByTypeUuid: this.selectedAttributeUuidsByTypeUuid,
                        groupedAttributeUuidsByTypeUuid: this.groupedAttributeUuidsByTypeUuid
                    }
                },
                {
                    name: this.$t('generateProductKeyWordsModal.steps.InputKeywordsStep'),
                    component: defineAsyncComponent(() => import('./wizardSteps/InputKeywordsStep.vue')),
                    props: {
                        collectedInfoByTypeUuid: this.collectedInfoByTypeUuid,
                        selectedAttributeUuidsByTypeUuid: this.selectedAttributeUuidsByTypeUuid,
                        nonGroupedAttributesByTypeUuid: this.nonGroupedAttributesByTypeUuid,
                        keywordsByTypeUuid: this.keywordsByTypeUuid,
                        groupedAttributesByTypeUuid: this.groupedAttributesByTypeUuid,
                        integrationLanguages: this.integrationLanguages
                    }
                },
                {
                    name: this.$t('generateProductKeyWordsModal.steps.KeywordsTableStep'),
                    component: defineAsyncComponent(() => import('./wizardSteps/KeywordsTableStep.vue')),
                    props: {
                        collectedInfoByTypeUuid: this.collectedInfoByTypeUuid,
                        keywordsByTypeUuid: this.keywordsByTypeUuid,
                        nonGroupedAttributesByTypeUuid: this.nonGroupedAttributesByTypeUuid,
                        groupedAttributesByTypeUuid: this.groupedAttributesByTypeUuid,
                        integrationLanguages: this.integrationLanguages
                    }
                },
                {
                    name: this.$t('generateProductKeyWordsModal.steps.RewriteKeywordsStep'),
                    component: defineAsyncComponent(() => import('./wizardSteps/RewriteKeywordsStep.vue')),
                    props: {
                        collectedInfoByTypeUuid: this.collectedInfoByTypeUuid,
                        keywordsByTypeUuid: this.keywordsByTypeUuid,
                        savingSettingsByTypeUuid: this.savingSettingsByTypeUuid,
                        integrationLanguages: this.integrationLanguages
                    },
                }
            ]
        },
        nonGroupedAttributesByTypeUuid() {
            if (!this.collectedInfoByTypeUuid) return {};
            let nonGroupedAttributesByTypeUuid = {};

            for (const [typeUuid, collectedInfo] of Object.entries(this.collectedInfoByTypeUuid)) {
                const selectedAttributeUuids = this.selectedAttributeUuidsByTypeUuid[typeUuid] || [];
                const groupedAttributeUuids = this.groupedAttributeUuidsByTypeUuid[typeUuid]?.flatMap(group => group) || [];

                let result = {};


                for (const [attributeUuid, attribute] of Object.entries(collectedInfo.attributes)) {
                    if (!selectedAttributeUuids.includes(attributeUuid) || groupedAttributeUuids.includes(attributeUuid)) continue;

                    result[attributeUuid] = attribute;
                }

                nonGroupedAttributesByTypeUuid[typeUuid] = result;
            }

            return nonGroupedAttributesByTypeUuid;
        },
        groupedAttributesByTypeUuid() {
            if (!this.collectedInfoByTypeUuid) return {};
            let groupedAttributesByTypeUuid = {};

            for (const [typeUuid, collectedInfo] of Object.entries(this.collectedInfoByTypeUuid)) {
                const groupedAttributeUuids = this.groupedAttributeUuidsByTypeUuid[typeUuid] || [];

                let groups = [];

                for (const group of groupedAttributeUuids) {
                    const combinationsMap = new Map();
                    const attributes = [];

                    for (const attributeUuid of group) {
                        const attributeInfo = collectedInfo.attributes[attributeUuid];
                        if (attributeInfo) {
                            attributes.push({
                                name: attributeInfo.name
                            });
                        }
                    }

                    Object.values(collectedInfo.products).forEach(product => {
                        let currentCombinationItems = [];

                        for (const attributeUuid of group) {
                            const attributeInfo = collectedInfo.attributes[attributeUuid];

                            const itemUuid = product.attributeItemUuids.find(uuid => attributeInfo.items[uuid]);

                            if (itemUuid) {
                                currentCombinationItems.push({
                                    uuid: itemUuid,
                                    name: attributeInfo.items[itemUuid].name
                                });
                            }
                        }

                        if (currentCombinationItems.length > 0) {
                            const comboKey = currentCombinationItems.map(item => item.name).join('-');

                            if (!combinationsMap.has(comboKey)) {
                                combinationsMap.set(comboKey, currentCombinationItems);
                            }
                        }
                    });

                    groups.push({
                        attributes: attributes,
                        items: Array.from(combinationsMap.values())
                    });
                }

                groupedAttributesByTypeUuid[typeUuid] = groups;
            }

            return groupedAttributesByTypeUuid;
        },
        shouldShowErrorTable() {
            return !this.processingStarted && this.badHttpHistory.length > 0;
        },
    },
    watch: {
        selectedAttributeUuidsByTypeUuid: {
            handler() {
                this.syncGroupsWithSelection();
            },
            deep: true
        }
    }
}
</script>